import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";
import { WAITLIST_LINE, WaitlistForm } from "@/components/Waitlist";

const Field = ({ id, label, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</Label>
    <Input id={id} data-testid={`${id}-input`} className="h-11 bg-white border-border focus-visible:ring-navy" {...props} />
  </div>
);

export default function AuthPage({ mode, invite }) {
  const isLogin = mode === "login";
  const [qLimit, setQLimit] = useState(0);
  useEffect(() => { if (!isLogin) api.get("/public/info").then((r) => setQLimit(r.data.monthly_question_limit)).catch(() => {}); }, [isLogin]);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const next = (sp.get("next") || "").startsWith("/") && !sp.get("next").startsWith("//") ? sp.get("next") : "";
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "", newsletter_opt_in: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={next || (user.role === "admin" ? "/admin" : "/app")} replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = isLogin ? { email: form.email, password: form.password } : { ...form, invite: invite?.slug || "" };
      const { data } = await api.post(`/auth/${isLogin ? "login" : "register"}`, body);
      setUser(data);
      navigate(next || (data.role === "admin" ? "/admin" : "/app"));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-navy text-white p-12 relative overflow-hidden">
        <img src={BRAND.logo} alt="Roots Marketing Co" className="h-20 w-auto self-start" />
        <img src={BRAND.lindsay} alt="Lindsay Fenn" className="mx-auto w-72 aspect-[4/5] object-cover object-top rounded-3xl shadow-2xl" data-testid="auth-lindsay-photo" />
        <div>
          <p className="font-serif text-3xl leading-snug">"Small budget, no marketing team, still full on a Tuesday. That's the goal."</p>
          <p className="mt-4 eyebrow !text-sun">Lindsay Fenn · Roots Marketing Co</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        {!isLogin && !invite && !BRAND.signupOpen ? (
          <div className="w-full max-w-md fade-up" data-testid="register-waitlist">
            <Link to="/" className="flex items-center gap-3 mb-12" data-testid="auth-brand-link-waitlist"><img src={BRAND.leaf} alt="" className="h-7 w-7" /><span className="font-serif text-xl text-navy">{BRAND.name}</span></Link>
            <div className="eyebrow mb-3">Coming soon</div>
            <h1 className="text-3xl sm:text-4xl text-navy mb-4">Join the waitlist.</h1>
            <p className="text-muted-foreground mb-8">{WAITLIST_LINE}</p>
            <WaitlistForm />
            <p className="mt-8 text-sm text-muted-foreground text-center">Already testing The Roots Club? <Link to="/login" className="text-pinkb hover:underline" data-testid="waitlist-login-link">Log in</Link></p>
          </div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid={`${mode}-form`}>
          <Link to="/" className="flex items-center gap-3 mb-12" data-testid="auth-brand-link"><img src={BRAND.leaf} alt="" className="h-7 w-7" /><span className="font-serif text-xl text-navy">{BRAND.name}</span></Link>
          <div className="eyebrow mb-3">{isLogin ? "Members' area" : invite?.trial_days > 0 ? `${invite.trial_days}-day free trial · no card needed` : "Free during the testing period"}</div>
          <h1 className="text-3xl sm:text-4xl text-navy mb-8">{isLogin ? "Welcome back." : "Join The Roots Club."}</h1>
          {invite && (
            <div className="-mt-4 mb-8 rounded-xl border-l-4 border-pinkb bg-pinkb/5 px-4 py-3" data-testid="invite-banner">
              <div className="eyebrow mb-1">You're invited · {invite.name}</div>
              {invite.welcome && <p className="text-sm text-navy" data-testid="invite-welcome">{invite.welcome}</p>}
              {invite.trial_days > 0 && <p className="mt-1 text-sm font-medium text-navy" data-testid="invite-trial-note">Your {invite.trial_days}-day free trial starts as soon as you create your account. No card needed.</p>}
            </div>
          )}
          <div className="space-y-5">
            {!isLogin && <Field id="register-name" label="Your name" value={form.name} onChange={set("name")} required />}
            {!isLogin && <Field id="register-business" label="Business or brand name" value={form.business_name} onChange={set("business_name")} />}
            <Field id={`${mode}-email`} label="Email" type="email" value={form.email} onChange={set("email")} required />
            <Field id={`${mode}-password`} label="Password" type="password" value={form.password} onChange={set("password")} required minLength={isLogin ? 1 : 8} />
            {isLogin && <div className="text-right -mt-2"><Link to="/forgot-password" className="text-sm text-pinkb hover:underline" data-testid="forgot-password-link">Forgotten your password?</Link></div>}
          </div>
          {!isLogin && (
            <label className="mt-5 flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={form.newsletter_opt_in} onChange={(e) => setForm({ ...form, newsletter_opt_in: e.target.checked })} data-testid="register-newsletter-checkbox" className="mt-1 accent-pinkb" />
              Send me Lindsay's newsletter with marketing tips and event news (optional, unsubscribe any time)
            </label>
          )}
          {!isLogin && qLimit > 0 && (
            <div className="mt-5 rounded-xl bg-secondary px-4 py-3 text-sm" data-testid="register-question-limit">
              <span className="font-medium text-navy">Includes a monthly Ask Lindsay question allowance.</span> It resets on the 1st. Pro members (£79/month) get unlimited questions and fortnightly calls with Lindsay. Free while we test.</div>
          )}
          {error && <p className="mt-4 text-sm text-rose-600" data-testid="auth-error">{error}</p>}
          <Button type="submit" disabled={busy} className="mt-8 w-full h-11 rounded-full bg-navy hover:bg-navy/90 text-white" data-testid={`${mode}-submit-btn`}>
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{isLogin ? "Log in" : "Create my free account"}
          </Button>
          <p className="mt-6 text-sm text-muted-foreground text-center">
            {isLogin ? "New here? " : "Already a member? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-pinkb hover:underline" data-testid="auth-switch-link">
              {isLogin ? (BRAND.signupOpen ? "Join the free beta" : "Join the waitlist") : "Log in"}
            </Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
